import React, { useEffect, useRef } from 'react';
import TextInput from '../components/TextInput';
import WheelDatePicker from '../components/WheelDatePicker';
import ChoicePicker from '../components/ChoicePicker';
import { sendIcon } from '../chatIcons';
import type { CareerHistorySurveyFlow } from './useCareerHistorySurveyFlow';
import type { CareerHistorySurveyStep } from './careerHistorySurveyTypes';

const AUTO_INTRO_MS = 550;

const YES_NO = (t: CareerHistorySurveyFlow['t']) => [
  { value: 'yes' as const, label: t('submitDocuments.careerHistorySurvey.yes') },
  { value: 'no' as const, label: t('submitDocuments.careerHistorySurvey.no') },
];

function OkBar({ onOk, ariaLabel }: { onOk: () => void; ariaLabel: string }) {
  return (
    <div className="ff-text-input">
      <div className="ff-text-input__footer">
        <button type="button" className="ff-text-input__send" onClick={onOk} aria-label={ariaLabel}>
          <img src={sendIcon} alt="" aria-hidden />
        </button>
      </div>
    </div>
  );
}

function SkipBar({
  skipLabel,
  onSkip,
  onOk,
  okDisabled,
  sendAria,
}: {
  skipLabel: string;
  onSkip: () => void;
  onOk: () => void;
  okDisabled?: boolean;
  sendAria: string;
}) {
  return (
    <div className="ff-text-input">
      <div className="ff-text-input__footer" style={{ justifyContent: 'space-between', gap: 8 }}>
        <button type="button" className="ff-text-input__ok" onClick={onSkip}>
          {skipLabel}
        </button>
        <button
          type="button"
          className="ff-text-input__send"
          onClick={onOk}
          disabled={okDisabled}
          aria-label={sendAria}
        >
          <img src={sendIcon} alt="" aria-hidden />
        </button>
      </div>
    </div>
  );
}

export default function CareerHistorySurveyChatComposer({
  flow,
  wheelLocale,
}: {
  flow: CareerHistorySurveyFlow;
  wheelLocale: string;
}) {
  const tokenRef = useRef(0);
  const { step, draft, t } = flow;
  const sendAria = t('submitDocuments.profileName.send');

  useEffect(() => {
    if (flow.isLoading || flow.reviewMode) return;
    if (step !== 'workHistoryIntro') return;
    const token = ++tokenRef.current;
    const id = window.setTimeout(() => {
      if (tokenRef.current !== token) return;
      void flow.skipIntro();
    }, AUTO_INTRO_MS);
    return () => window.clearTimeout(id);
  }, [flow, step]);

  if (flow.isLoading) return null;
  if (flow.isComplete) return null;
  if (flow.reviewMode && flow.editIndex === null && !flow.inPneuFlow) return null;
  if (flow.showFieldEdit) return null;
  if (step === 'workHistoryIntro') return null;

  if (step === 'recordGate') {
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={flow.recordGateAnswer || null}
          options={YES_NO(t)}
          onChange={(v) => void flow.handleRecordGate(v)}
        />
      </div>
    );
  }

  const textStep = (
    field: keyof typeof draft,
    from: CareerHistorySurveyStep,
    labelKey: string,
    required = true
  ) => (
    <TextInput
      fields={[
        {
          id: field,
          label: t(labelKey),
          required,
          hideLabel: true,
          ariaLabel: t(labelKey),
        },
      ]}
      values={{ [field]: String(draft[field] ?? '') }}
      onChange={(_id, v) => flow.setDraft((d) => ({ ...d, [field]: v }))}
      onSubmit={() => void flow.submitTextField(field, String(draft[field] ?? '').trim(), from)}
      submitDisabled={required && !String(draft[field] ?? '').trim()}
      submitAriaLabel={sendAria}
      requiredMessage={t('submitDocuments.profileName.validation.required')}
    />
  );

  if (step === 'siteName') {
    return textStep('siteName', 'siteName', 'submitDocuments.careerHistorySurvey.fields.siteName');
  }
  if (step === 'location') {
    return textStep('location', 'location', 'submitDocuments.careerHistorySurvey.fields.location');
  }
  if (step === 'contractor') {
    return textStep('contractor', 'contractor', 'submitDocuments.careerHistorySurvey.fields.contractor');
  }
  if (step === 'companyName') {
    return textStep('companyName', 'companyName', 'submitDocuments.careerHistorySurvey.fields.companyName');
  }
  if (step === 'incharge') {
    return textStep('incharge', 'incharge', 'submitDocuments.careerHistorySurvey.fields.incharge');
  }
  if (step === 'workPerformed') {
    return textStep('workPerformed', 'workPerformed', 'submitDocuments.careerHistorySurvey.fields.workPerformed');
  }

  if (step === 'startDate') {
    return (
      <>
        <WheelDatePicker
          value={flow.startDateWheel}
          onChange={flow.setStartDateWheel}
          locale={wheelLocale}
        />
        <OkBar onOk={() => void flow.handleStartDate()} ariaLabel={sendAria} />
      </>
    );
  }

  if (step === 'endDate') {
    return (
      <>
        <WheelDatePicker value={flow.endDateWheel} onChange={flow.setEndDateWheel} locale={wheelLocale} />
        <SkipBar
          skipLabel={t('submitDocuments.careerHistorySurvey.currentJobSkip')}
          onSkip={() => void flow.skipEndDate()}
          onOk={() => void flow.handleEndDate()}
          sendAria={sendAria}
        />
      </>
    );
  }

  if (step === 'entrySummary' || step === 'dustDuration') {
    return <OkBar onOk={() => void flow.confirmEntrySummary()} ariaLabel={sendAria} />;
  }

  if (step === 'pneuGate') {
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={flow.pneuDraft.gateAnswer || null}
          options={YES_NO(t)}
          onChange={(v) => void flow.handlePneuGate(v)}
        />
      </div>
    );
  }

  if (step === 'pneu1Date') {
    return (
      <>
        <WheelDatePicker value={flow.pneu1DateWheel} onChange={flow.setPneu1DateWheel} locale={wheelLocale} />
        <OkBar onOk={() => void flow.handlePneu1Date()} ariaLabel={sendAria} />
      </>
    );
  }

  if (step === 'pneu1Contractor') {
    return (
      <TextInput
        fields={[
          {
            id: 'contractor',
            label: t('submitDocuments.careerHistorySurvey.fields.pneuContractor'),
            required: true,
            hideLabel: true,
            ariaLabel: t('submitDocuments.careerHistorySurvey.fields.pneuContractor'),
          },
        ]}
        values={{ contractor: flow.pneuDraft.slot1.contractor }}
        onChange={(_id, v) => flow.setPneuDraft((d) => ({ ...d, slot1: { ...d.slot1, contractor: v } }))}
        onSubmit={() =>
          void flow.submitPneuText(1, 'contractor', 'pneu1Contractor', flow.pneuDraft.slot1.contractor.trim())
        }
        submitDisabled={!flow.pneuDraft.slot1.contractor.trim()}
        submitAriaLabel={sendAria}
        requiredMessage={t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  if (step === 'pneu1Findings') {
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={flow.pneuDraft.slot1.findings || null}
          options={YES_NO(t)}
          onChange={(v) => void flow.handlePneuFindings(1, 'pneu1Findings', v)}
        />
      </div>
    );
  }

  if (step === 'pneu1Area') {
    return (
      <TextInput
        fields={[
          {
            id: 'area',
            label: t('submitDocuments.careerHistorySurvey.fields.pneuArea'),
            required: true,
            hideLabel: true,
            ariaLabel: t('submitDocuments.careerHistorySurvey.fields.pneuArea'),
          },
        ]}
        values={{ area: flow.pneuDraft.slot1.area }}
        onChange={(_id, v) => flow.setPneuDraft((d) => ({ ...d, slot1: { ...d.slot1, area: v } }))}
        onSubmit={() => void flow.submitPneuText(1, 'area', 'pneu1Area', flow.pneuDraft.slot1.area.trim())}
        submitDisabled={!flow.pneuDraft.slot1.area.trim()}
        submitAriaLabel={sendAria}
        requiredMessage={t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  if (step === 'pneu2Date') {
    return (
      <>
        <WheelDatePicker value={flow.pneu2DateWheel} onChange={flow.setPneu2DateWheel} locale={wheelLocale} />
        <SkipBar
          skipLabel={t('submitDocuments.careerHistorySurvey.pneuSlot2SkipButton')}
          onSkip={() => void flow.skipPneu2()}
          onOk={() => void flow.handlePneu2Date()}
          sendAria={sendAria}
        />
      </>
    );
  }

  if (step === 'pneu2Contractor') {
    return (
      <TextInput
        fields={[
          {
            id: 'contractor',
            label: t('submitDocuments.careerHistorySurvey.fields.pneuContractor'),
            required: true,
            hideLabel: true,
            ariaLabel: t('submitDocuments.careerHistorySurvey.fields.pneuContractor'),
          },
        ]}
        values={{ contractor: flow.pneuDraft.slot2.contractor }}
        onChange={(_id, v) => flow.setPneuDraft((d) => ({ ...d, slot2: { ...d.slot2, contractor: v } }))}
        onSubmit={() =>
          void flow.submitPneuText(2, 'contractor', 'pneu2Contractor', flow.pneuDraft.slot2.contractor.trim())
        }
        submitDisabled={!flow.pneuDraft.slot2.contractor.trim()}
        submitAriaLabel={sendAria}
        requiredMessage={t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  if (step === 'pneu2Findings') {
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={flow.pneuDraft.slot2.findings || null}
          options={YES_NO(t)}
          onChange={(v) => void flow.handlePneuFindings(2, 'pneu2Findings', v)}
        />
      </div>
    );
  }

  if (step === 'pneu2Area') {
    return (
      <TextInput
        fields={[
          {
            id: 'area',
            label: t('submitDocuments.careerHistorySurvey.fields.pneuArea'),
            required: true,
            hideLabel: true,
            ariaLabel: t('submitDocuments.careerHistorySurvey.fields.pneuArea'),
          },
        ]}
        values={{ area: flow.pneuDraft.slot2.area }}
        onChange={(_id, v) => flow.setPneuDraft((d) => ({ ...d, slot2: { ...d.slot2, area: v } }))}
        onSubmit={() => void flow.submitPneuText(2, 'area', 'pneu2Area', flow.pneuDraft.slot2.area.trim())}
        submitDisabled={!flow.pneuDraft.slot2.area.trim()}
        submitAriaLabel={sendAria}
        requiredMessage={t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  return null;
}
